import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';
import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';
import ToggleButton from '@rio-cloud/rio-uikit/ToggleButton';
import isNil from 'lodash/fp/isNil';
import isUndefined from 'lodash/fp/isUndefined';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { LabeledNumberInput } from '../../components/LabeledNumberInput/LabeledNumberInput';
import { LabeledSelect } from '../../components/LabeledSelect/LabeledSelect';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { VehicleType2 } from '../../store/facade/facadeApiV2';
import { PlugType, TunnelCategory } from '../../store/facade/facadeApiV3';
import {
    selectAssetChargePortTypes,
    selectAssetTemperature,
    selectCargoWeightField,
    selectDeviceId,
    selectEmptyWeightField,
    selectHazardClassification,
    selectHeightField,
    selectIsEV,
    selectLengthField,
    selectMaxFreightTrainWeightField,
    selectOutdoorTemperature,
    selectPlugTypeField,
    selectProfileType,
    selectTotalWeightField,
    selectTrailerAxlesField,
    selectTrailerField,
    selectTruckTypeField,
    selectTunnelCategory,
    selectVehicleAxlesField,
    selectWidthField,
} from '../../store/vehicleProfile/vehicleProfileSelectors';
import {
    changeAverageOutdoorTemperature,
    changeCargoWeight,
    changeDeviceId,
    changeEmptyWeight,
    changeHazardClassification,
    changeHeight,
    changeLength,
    changePlugType,
    changeTrailer,
    changeTrailerAxles,
    changeTruckType,
    changeTunnelCategory,
    changeVehicleAxles,
    changeWidth,
} from '../../store/vehicleProfile/vehicleProfileSlice';
import {
    HazardClass,
    HEREVehicleAttibuteNames,
    VEHCILE_ATTRIBUTES_UNITS,
    VehicleType,
} from '../../store/vehicleProfile/vehicleProfileTypes';

// --- Tunnel Category ---

const TunnelCategoryOptions = [
    { id: TunnelCategory.All, label: TunnelCategory.All },
    { id: TunnelCategory.B, label: TunnelCategory.B },
    { id: TunnelCategory.C, label: TunnelCategory.C },
    { id: TunnelCategory.D, label: TunnelCategory.D },
    { id: TunnelCategory.E, label: TunnelCategory.E },
];

export const TunnelCategoryField = () => {
    const dispatch = useAppDispatch();
    const tunnelCategory = useAppSelector(selectTunnelCategory);

    return (
        <LabeledSelect
            className='flex-1-1-0'
            dataTestId='tunnel-category-field'
            label={<FormattedMessage id='intl-msg:smartRoute.vehicle.tunnelCategory' />}
            optionLabels={TunnelCategoryOptions}
            selectedOption={tunnelCategory}
            onChange={value => dispatch(changeTunnelCategory(value))}
        />
    );
};

// --- Hazard Classification ---

const hazardClassOptionLabels = [
    { id: HazardClass.NONE, label: <FormattedMessage id='intl-msg:smartRoute.generic.none' /> },
    {
        id: HazardClass.EXPLOSIVE,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.explosive' />,
    },
    {
        id: HazardClass.GAS,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.gas' />,
    },
    {
        id: HazardClass.FLAMMABLE_LIQUID,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.flammableLiquid' />,
    },
    {
        id: HazardClass.FLAMMABLE_SOLID,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.flammableSolid' />,
    },
    {
        id: HazardClass.OXIDIZING_AGENT,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.oxidizingAgent' />,
    },
    {
        id: HazardClass.POISONOUS_INFECTIOUS,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.poisonousInfectious' />,
    },
    {
        id: HazardClass.RADIOACTIVE,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.radioactive' />,
    },
    {
        id: HazardClass.CORROSIVE,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.corrosive' />,
    },
    {
        id: HazardClass.POISONOUS_INHALATION,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.poisonousInhalation' />,
    },
    {
        id: HazardClass.HARMFUL_TO_WATER,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.harmfulToWater' />,
    },
    {
        id: HazardClass.ORGANIC,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification.organic' />,
    },
];

export const HazardClassificationField = () => {
    const dispatch = useAppDispatch();
    const hazardClass = useAppSelector(selectHazardClassification);

    return (
        <LabeledSelect
            className='flex-1-1-0'
            dataTestId='hazard-classification-field'
            label={<FormattedMessage id='intl-msg:smartRoute.vehicle.dangerousGoodsClassification' />}
            optionLabels={hazardClassOptionLabels}
            selectedOption={hazardClass}
            onChange={value => dispatch(changeHazardClassification(value as HazardClass))}
        />
    );
};

// --- Cargo Weight ---

export const CargoWeightField = () => {
    const dispatch = useAppDispatch();
    const cargoWeight = useAppSelector(selectCargoWeightField);
    const emptyWeight = useAppSelector(selectEmptyWeightField);
    const maxFreightTrainWeight = useAppSelector(selectMaxFreightTrainWeightField);

    const maxCargoWeightInKg =
        maxFreightTrainWeight.value !== undefined && emptyWeight.value !== undefined
            ? maxFreightTrainWeight.value * 1000 - emptyWeight.value
            : undefined;

    const hasExceededMaxCargo = maxCargoWeightInKg !== undefined && (cargoWeight.value ?? 0) > maxCargoWeightInKg;

    const handleCargoWeightChange = (value: number) => {
        dispatch(changeCargoWeight(value));
    };

    return (
        <div
            className={`form-group flex-1-1-0 has-feedback ${hasExceededMaxCargo ? 'has-error' : ''}`}
            data-testid='input-cargo-weight'
        >
            <label className='control-label'>
                <FormattedMessage id='intl-msg:smartRoute.vehicle.cargoWeight' />
            </label>
            <div className='input-group'>
                <input
                    type='text'
                    className='form-control'
                    inputMode='decimal'
                    value={cargoWeight.value ?? 0}
                    onChange={e => {
                        const parsed = Number.parseInt(e.target.value, 10);
                        if (!Number.isNaN(parsed) && parsed >= 0) {
                            handleCargoWeightChange(parsed);
                        }
                    }}
                    disabled={!cargoWeight.isEditable}
                />
                <div className='input-group-addon display-flex align-items-center gap-5'>
                    {hasExceededMaxCargo && (
                        <span className='rioglyph rioglyph-error-sign text-color-danger' aria-hidden='true' />
                    )}
                    <span>{VEHCILE_ATTRIBUTES_UNITS[HEREVehicleAttibuteNames.CARGO_WEIGHT]}</span>
                </div>
            </div>

            {hasExceededMaxCargo && (
                <span className='help-block'>
                    <FormattedMessage
                        id='intl-msg:smartRoute.vehicle.cargoWeight.exceeded'
                        values={{ max: maxCargoWeightInKg }}
                    />
                </span>
            )}
        </div>
    );
};

// --- Empty Weight ---

export const EmptyWeightField = () => {
    const dispatch = useAppDispatch();
    const emptyWeight = useAppSelector(selectEmptyWeightField);

    return (
        <LabeledNumberInput
            id='input-empty-weight'
            className='flex-1-1-0'
            labelId='intl-msg:smartRoute.vehicle.emptyWeight'
            value={emptyWeight.value}
            unit={VEHCILE_ATTRIBUTES_UNITS[HEREVehicleAttibuteNames.EMPTY_WEIGHT]}
            onChange={value => dispatch(changeEmptyWeight(value))}
            disabled={!emptyWeight.isEditable}
        />
    );
};

// --- Total Weight ---

export const TotalWeightField = () => {
    const totalWeight = useAppSelector(selectTotalWeightField);

    return (
        <LabeledNumberInput
            integerOnly
            id='input-total-weight'
            className='flex-1-1-0'
            labelId='intl-msg:smartRoute.vehicle.totalWeight'
            value={totalWeight.value}
            unit={VEHCILE_ATTRIBUTES_UNITS[HEREVehicleAttibuteNames.TOTAL_WEIGHT]}
            disabled={!totalWeight.isEditable}
            labelAddon={
                <SimpleTooltip
                    content={<FormattedMessage id='intl-msg:smartRoute.vehicle.totalWeight.tooltip' />}
                    placement='bottom'
                >
                    <span className='rioglyph rioglyph-question-sign margin-left-5 text-size-12' />
                </SimpleTooltip>
            }
        />
    );
};

// --- Height ---

export const HeightField = () => {
    const dispatch = useAppDispatch();
    const height = useAppSelector(selectHeightField);

    return (
        <LabeledNumberInput
            id='input-height'
            className='flex-1-1-0'
            labelId='intl-msg:smartRoute.vehicle.height'
            value={height.value}
            unit={VEHCILE_ATTRIBUTES_UNITS[HEREVehicleAttibuteNames.HEIGHT]}
            onChange={value => dispatch(changeHeight(value))}
            disabled={!height.isEditable}
        />
    );
};

// --- Width ---

export const WidthField = () => {
    const dispatch = useAppDispatch();
    const width = useAppSelector(selectWidthField);

    return (
        <LabeledNumberInput
            id='input-width'
            className='flex-1-1-0'
            labelId='intl-msg:smartRoute.vehicle.width'
            value={width.value}
            unit={VEHCILE_ATTRIBUTES_UNITS[HEREVehicleAttibuteNames.WIDTH]}
            onChange={value => dispatch(changeWidth(value))}
            disabled={!width.isEditable}
        />
    );
};

// --- Length ---

export const LengthField = () => {
    const dispatch = useAppDispatch();
    const length = useAppSelector(selectLengthField);

    return (
        <LabeledNumberInput
            id='input-length'
            className='flex-1-1-0'
            labelId='intl-msg:smartRoute.vehicle.totalLength'
            value={length.value}
            unit={VEHCILE_ATTRIBUTES_UNITS[HEREVehicleAttibuteNames.LENGTH]}
            onChange={value => dispatch(changeLength(value))}
            disabled={!length.isEditable}
        />
    );
};

// --- Plug Type ---

export const PlugTypeField = () => {
    const dispatch = useAppDispatch();
    const plugTypeField = useAppSelector(selectPlugTypeField);
    const assetChargePortTypes = useAppSelector(selectAssetChargePortTypes);

    const isAssetSelected = !plugTypeField.isEditable && assetChargePortTypes.length > 0;

    const [isCcsActive, setIsCcsActive] = useState(() => {
        return !plugTypeField.value || plugTypeField.value === PlugType.CcsCombo2;
    });
    const [isMcsActive, setIsMcsActive] = useState(() => {
        return plugTypeField.value === PlugType.Mcs;
    });

    useEffect(() => {
        if (isAssetSelected) {
            return;
        }
        if (isCcsActive && !isMcsActive) {
            dispatch(changePlugType({ value: PlugType.CcsCombo2, isEditable: true }));
        } else if (!isCcsActive && isMcsActive) {
            dispatch(changePlugType({ value: PlugType.Mcs, isEditable: true }));
        } else if (isCcsActive && isMcsActive) {
            dispatch(changePlugType({ value: PlugType.CcsCombo2, isEditable: true }));
        }
    }, [isCcsActive, isMcsActive, isAssetSelected, dispatch]);

    const handleCcsClick = () => {
        if (!isCcsActive && !isMcsActive) {
            setIsCcsActive(true);
            return;
        }
        setIsCcsActive(prev => !prev);
    };

    const handleMcsClick = () => {
        if (!isCcsActive && !isMcsActive) {
            setIsMcsActive(true);
            return;
        }
        setIsMcsActive(prev => !prev);
    };

    return (
        <div className='form-group flex-1-1-0' data-testid='plug-type-field'>
            <label className='control-label'>
                <FormattedMessage id='intl-msg:smartRoute.generic.plugtype' />
            </label>
            <div className='display-flex flex-wrap gap-10'>
                {isAssetSelected ? (
                    assetChargePortTypes.map(portType => (
                        <ToggleButton key={portType} active={true} disabled>
                            {portType.toUpperCase()}
                        </ToggleButton>
                    ))
                ) : (
                    <>
                        <ToggleButton active={isCcsActive} onClick={handleCcsClick}>
                            CCS_COMBO_2
                        </ToggleButton>
                        <ToggleButton active={isMcsActive} onClick={handleMcsClick}>
                            MCS
                        </ToggleButton>
                    </>
                )}
            </div>
        </div>
    );
};

// --- Vehicle Type ---

const vehicleType2Options = [
    {
        id: VehicleType2.StraightTruck,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.truckType.chassis' />,
    },
    {
        id: VehicleType2.Tractor,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.truckType.tractor' />,
    },
];

export const VehicleTypeField = () => {
    const dispatch = useAppDispatch();
    const selectedType = useAppSelector(selectTruckTypeField);

    return (
        <LabeledSelect
            className='flex-1-1-0'
            dataTestId='vehicle-type-field'
            label={<FormattedMessage id='intl-msg:smartRoute.vehicle.vehicleType' />}
            optionLabels={vehicleType2Options}
            selectedOption={selectedType.value}
            onChange={value => dispatch(changeTruckType(value as VehicleType2))}
            disabled={!selectedType.isEditable}
        />
    );
};

// --- Trailer ---

const TrailerOption = {
    YES: 'YES',
    NO: 'NO',
} as const;

const trailerOptions = [
    { id: TrailerOption.YES, label: <FormattedMessage id='intl-msg:smartRoute.generic.yes' /> },
    { id: TrailerOption.NO, label: <FormattedMessage id='intl-msg:smartRoute.generic.no' /> },
];

export const TrailerField = () => {
    const dispatch = useAppDispatch();
    const { value: selectedTrailer, isEditable: isTrailerEditable } = useAppSelector(selectTrailerField);

    const getOption = () => {
        if (isUndefined(selectedTrailer)) {
            return undefined;
        }
        return selectedTrailer ? TrailerOption.YES : TrailerOption.NO;
    };

    return (
        <LabeledSelect
            className='flex-1-1-0'
            dataTestId='trailer-field'
            label={<FormattedMessage id='intl-msg:smartRoute.vehicle.trailer' />}
            optionLabels={trailerOptions}
            selectedOption={getOption()}
            onChange={() => dispatch(changeTrailer(!selectedTrailer))}
            disabled={!isTrailerEditable}
        />
    );
};

// --- Vehicle Axles ---

export const VehicleAxlesField = () => {
    const dispatch = useAppDispatch();
    const vehicleAxles = useAppSelector(selectVehicleAxlesField);

    return (
        <LabeledNumberInput
            id='input-vehicle-axles'
            className='flex-1-1-0'
            labelId='intl-msg:smartRoute.vehicle.vehicleAxles'
            unitIcon='axle'
            value={vehicleAxles.value}
            integerOnly
            onChange={value => dispatch(changeVehicleAxles(value))}
            disabled={!vehicleAxles.isEditable}
        />
    );
};

// --- Trailer Axles ---

export const TrailerAxlesField = () => {
    const dispatch = useAppDispatch();
    const trailerAxles = useAppSelector(selectTrailerAxlesField);

    return (
        <LabeledNumberInput
            id='input-trailer-axles'
            className='flex-1-1-0'
            labelId='intl-msg:smartRoute.vehicle.trailerAxles'
            unitIcon='axle'
            value={trailerAxles.value}
            integerOnly
            onChange={value => dispatch(changeTrailerAxles(value))}
            disabled={!trailerAxles.isEditable}
        />
    );
};

// --- Outdoor Temperature ---

export const OutdoorTemperatureField = () => {
    const dispatch = useAppDispatch();
    const outdoorTemperature = useAppSelector(selectOutdoorTemperature);
    const assetTemperature = useAppSelector(selectAssetTemperature);
    const isDisabled = !isNil(assetTemperature);

    return (
        <LabeledNumberInput
            integerOnly
            id='input-outdoor-temperature'
            className='flex-1-1-0'
            labelId='intl-msg:smartRoute.vehicle.outdoorTemperature'
            value={outdoorTemperature}
            unit={VEHCILE_ATTRIBUTES_UNITS[HEREVehicleAttibuteNames.OUTDOOR_TEMPERATURE]}
            onChange={value => dispatch(changeAverageOutdoorTemperature(value))}
            disabled={isDisabled}
        />
    );
};

// --- Device ID ---

export const DeviceIdField = () => {
    const dispatch = useAppDispatch();
    const deviceId = useAppSelector(selectDeviceId);

    return (
        <div className='form-group' data-testid='device-id-field'>
            <label htmlFor='device-id'>DeviceId</label>
            <div>
                <ClearableInput value={deviceId} onChange={(newValue: string) => dispatch(changeDeviceId(newValue))} />
            </div>
        </div>
    );
};

// --- Profile Select ---

export const vehicleTypeOptionLabels = [
    {
        id: VehicleType.TRUCK_AND_TRAILER_40T,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.vehicleType.truckAndTrailer40T' />,
    },
    { id: VehicleType.TRUCK_11T, label: <FormattedMessage id='intl-msg:smartRoute.vehicle.vehicleType.truck11T' /> },
    { id: VehicleType.TRUCK_7T5, label: <FormattedMessage id='intl-msg:smartRoute.vehicle.vehicleType.truck7T5' /> },
    {
        id: VehicleType.TRANSPORTER_3T5,
        label: <FormattedMessage id='intl-msg:smartRoute.vehicle.vehicleType.transporter3T5' />,
    },
    { id: VehicleType.CAR, label: <FormattedMessage id='intl-msg:smartRoute.vehicle.vehicleType.car' /> },
    { id: VehicleType.CUSTOM_ICE, label: <FormattedMessage id='intl-msg:smartRoute.vehicle.vehicleType.customICE' /> },
];

export const customVehicleOption = {
    id: VehicleType.CUSTOM_EV,
    label: <FormattedMessage id='intl-msg:smartRoute.vehicle.vehicleType.customEV' />,
};

interface ProfileFieldProps {
    evOptions: SelectOption[];
    iceOptions: SelectOption[];
    handleProfileChange: (option: string) => void;
    disabled?: boolean;
}

export const ProfileField = ({ evOptions, iceOptions, handleProfileChange, disabled }: ProfileFieldProps) => {
    const isEV = useAppSelector(selectIsEV).value;
    const vehicleType = useAppSelector(selectProfileType);

    if (isEV ? isNil(evOptions) : isNil(iceOptions)) {
        return null;
    }

    return (
        <LabeledSelect
            className='width-100pct'
            dataTestId='profile-field'
            disabled={disabled}
            label={<FormattedMessage id='intl-msg:smartRoute.vehicle.profile' />}
            optionLabels={isEV ? evOptions : iceOptions}
            selectedOption={vehicleType}
            placeholder={<FormattedMessage id='intl-msg:smartRoute.vehicle.selectVehicleType' />}
            onChange={option => handleProfileChange(option)}
        />
    );
};
